// V2 rate-card workbook (Rate Card Modification MoM, Sep 2026) — pure helpers.
//
// ONE workbook for Forward + RVP replaces the one-sheet-per-parent×service format:
//   T&C              read-only summary of the commercials entered in the UI (sheet-protected)
//   Courier pricing  zones × weight slabs down, courier PARENT columns across,
//                    grouped in bands: Surface · Air · SDD · NDD
//   RVP              same matrix, Reverse band (editable in Mode B; locked info
//                    sheet in Mode A — values derive from the forward ladder)
//   COD              only when COD = VARIABLE: order-value bands per column
//   RTO              only when RTO = VARIABLE: % of forward per zone × slab
//   __META__         hidden JSON — exact column ↔ (parent × service × codes) map,
//                    so parsing never guesses from display labels
//
// Generation uses exceljs (styles + frozen panes + enforced sheet protection,
// matching the product team's 0_blank_template look). Parsing stays on SheetJS,
// and the parser emits the SAME combo model the legacy parser produced, so
// buildApplyPayload / preview / save-rate-card stay byte-identical downstream.
import * as XLSX from 'xlsx';
import { normalizeStCode } from '../constants';
import {
  ZONES, slabsForTemplate, resolveTcForParent, selectedCodesForService,
} from './rateEngine';

export const V2_SHEETS = {
  TC: 'T&C',
  PRICING: 'Courier pricing',
  RVP: 'RVP',
  COD: 'COD',
  RTO: 'RTO',
  // Custom-slab parents' sheets — same layouts, but the slab ladder (Min/Max)
  // is editable and shared by every custom parent.
  PRICING_CUSTOM: 'Courier pricing (Custom)',
  RVP_CUSTOM: 'RVP (Custom)',
  COD_CUSTOM: 'COD (Custom)',
  RTO_CUSTOM: 'RTO (Custom)',
  META: '__META__',
};


export const BAND_ORDER = ['SURFACE', 'AIR', 'SDD', 'NDD'];
export const ALL_COLUMN_LABEL = '*ALL*';

// Band for a service id. SDD and NDD are separate bands (MoM item B); 2–4hr and
// sub-2hr ride under AIR (current behaviour, D10).
export function bandOfService(svcId) {
  const s = String(svcId || '').toUpperCase();
  if (s.startsWith('REV_')) return 'REVERSE';
  if (s.startsWith('SDD_')) return 'SDD';
  if (s.includes('NDD')) return 'NDD';
  if (s.includes('AIR') || s === 'H2TO4' || s === 'SUB2H') return 'AIR';
  return 'SURFACE';
}

// (parent, serviceId, codes[]) tuples the merchant selected on the Profile step,
// split by band. Mirrors the account-code selection rules of the legacy model.
function tuplesByBand(wizard) {
  const byBand = { SURFACE: [], AIR: [], SDD: [], NDD: [], REVERSE: [] };
  (wizard.resolvedServices || []).forEach((s) => {
    if (!s || !s.id) return;
    const band = bandOfService(s.id);
    const matching = (wizard.serviceabilityGroups || [])
      .filter((g) => normalizeStCode(g.code) === normalizeStCode(s.id));
    const active = matching.flatMap((g) => g.linkedCodes || []).filter((lc) => lc.isActive !== false);
    const allCodes = active.map((lc) => lc.cpAccountCode);
    const selected = new Set(selectedCodesForService(wizard.accountCodeSelection, s.id, allCodes));
    const perParent = {};
    active.forEach((lc) => {
      if (!selected.has(lc.cpAccountCode)) return;
      const p = lc.parent || lc.cpAccountCode;
      if (!perParent[p]) perParent[p] = [];
      perParent[p].push(lc.cpAccountCode);
    });
    Object.entries(perParent).forEach(([parent, codes]) => {
      byBand[band].push({ parent, serviceId: s.id, codes });
    });
  });
  return byBand;
}

// Column plan for one matrix sheet. Distinct parent names stay AS-IS (no
// small/capital merging — per the clearing discussion).
//
// ONE unique parent = ONE column per band, consolidated from the Profile stage —
// no Regular/Large or lane variants in the sheet. The filled ladder fans out to
// ALL of that parent's selected accounts across ALL its services in the band,
// with no weight gate or validation here: which account serves which shipment
// (e.g. the >5 kg handover to a Large account) is the allocation engine's job
// at ordering time (business decision, Sep 2026).
function columnsForBand(tuples, blended) {
  if (!tuples.length) return [];
  if (blended) return [{ label: ALL_COLUMN_LABEL, all: true, entries: tuples.map(({ parent, serviceId }) => ({ parent, serviceId })) }];
  const perParent = {};
  tuples.forEach((t) => {
    if (!perParent[t.parent]) perParent[t.parent] = [];
    perParent[t.parent].push(t);
  });
  return Object.keys(perParent).map((parent) => ({
    label: String(parent),
    all: false,
    entries: perParent[parent].map((t) => ({ parent, serviceId: t.serviceId })),
  }));
}

// Unique parent names in the merchant's Profile-stage selection (all bands,
// Reverse included) — the option list for the custom-slab multi-select.
export function uniqueSelectedParents(wizard) {
  const byBand = tuplesByBand(wizard);
  const set = new Set();
  Object.values(byBand).forEach((ts) => ts.forEach((t) => set.add(t.parent)));
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function buildWorkbookModelV2(wizard) {
  const byBand = tuplesByBand(wizard);
  const blended = wizard.rateCardMode === 'blended';
  // Custom-slab parents (e.g. DTDC): their columns move to the "(Custom)" sheets,
  // whose slab ladder is editable. ONE shared custom ladder for all of them.
  const cs = wizard.customSlabParents || {};
  const customSet = new Set(cs.enabled ? (cs.parents || []) : []);
  const split = (tuples) => ({
    def: tuples.filter((t) => !customSet.has(t.parent)),
    cust: tuples.filter((t) => customSet.has(t.parent)),
  });
  const mkBands = (pick) => BAND_ORDER
    .map((band) => ({ band, columns: columnsForBand(pick(split(byBand[band])), blended) }))
    .filter((b) => b.columns.length);
  const bands = mkBands((s) => s.def);
  const customBands = mkBands((s) => s.cust);
  const rvpEnabled = !!(wizard.rvp && wizard.rvp.enabled);
  const rvpMode = (wizard.rvp && wizard.rvp.mode) || 'forward_linked';
  // RVP columns are always per parent×service (reverse cards are courier-wise even
  // when the forward card is blended — same rule the legacy RVP tab used).
  const revSplit = split(byBand.REVERSE);
  const rvpColumns = rvpEnabled ? columnsForBand(revSplit.def, false) : [];
  const customRvpColumns = rvpEnabled ? columnsForBand(revSplit.cust, false) : [];
  return {
    blended,
    bands,
    customBands,
    customParents: [...customSet].sort((a, b) => a.localeCompare(b)),
    rvpEnabled,
    rvpMode,
    rvpColumns,
    customRvpColumns,
    codMode: wizard.codChargeMode === 'VARIABLE' ? 'VARIABLE' : 'FIXED',
    rtoMode: wizard.rtoChargeMode === 'VARIABLE' ? 'VARIABLE' : 'FIXED',
    zones: ZONES,
    slabs: slabsForTemplate(wizard),
  };
}

// ── Style scheme (lifted verbatim from the product team's 0_blank_template) ──
const FIXED_COLS = 3; // Zone/Band | Min | Max

const BAND_FILL = { SURFACE: '1F3A5F', AIR: '2E6E8E', SDD: '3F6F52', NDD: '7A5230', REVERSE: '5B3A6E' };
const PALETTE = {
  fixed: 'E7ECF3',   // Zone/Min/Max columns + spacer corner
  zebra: 'F4F7FA',   // even data rows
  special: 'D9E2EC', // Unit / Charge-Type rows
  ink: '0F2C43',     // fixed-area text
  data: '1A1A1A',    // data text
  tcSection: '2E5C8A',
  prefill: 'EAF3FB', // read-only prefilled values
  mustfill: 'FFF2CC', // editable values the ops team types from the agreement
  grid: 'D9D9D9',
};
const argb = (hex) => ({ argb: 'FF' + hex });
const solid = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: argb(hex) });
const FONT = (o = {}) => ({ name: 'Calibri', size: 10, color: argb(PALETTE.data), ...o });
const THIN_BOTTOM = { bottom: { style: 'thin', color: argb(PALETTE.grid) } };

// Column layout for a matrix sheet: [{band, startCol, endCol}] (1-based, data cols only).
function bandRanges(bands) {
  const out = [];
  let c = FIXED_COLS + 1;
  bands.forEach(({ band, columns }) => {
    out.push({ band, startCol: c, endCol: c + columns.length - 1 });
    c += columns.length;
  });
  return out;
}

// Shared matrix-sheet painter: band row (merged, band-coloured), header row,
// fixed-column tinting, zebra striping + thin row borders, widths, frozen panes.
// `unlock(r, c)` marks the genuinely fillable cells; everything else stays locked
// so the sheet-protection applied afterwards freezes all UI-derived structure
// (zones, slabs, headers, unit rows) — "what the UI filled cannot change here".
function paintMatrix(ws, bands, { dataStartRow, freezeRows, fixedWidth = 11, unlock }) {
  const ranges = bandRanges(bands);
  const width = FIXED_COLS + bands.reduce((n, b) => n + b.columns.length, 0);
  ranges.forEach((r) => {
    if (r.endCol > r.startCol) ws.mergeCells(1, r.startCol, 1, r.endCol);
  });
  const bandOf = (col) => ranges.find((r) => col >= r.startCol && col <= r.endCol);
  const lastRow = ws.rowCount;
  for (let r = 1; r <= lastRow; r += 1) {
    const row = ws.getRow(r);
    for (let c = 1; c <= width; c += 1) {
      const cell = row.getCell(c);
      const inFixed = c <= FIXED_COLS;
      if (unlock && unlock(r, c)) cell.protection = { locked: false };
      if (r === 1) {
        cell.fill = inFixed ? solid(PALETTE.fixed) : solid(BAND_FILL[(bandOf(c) || {}).band] || PALETTE.fixed);
        cell.font = FONT({ bold: true, color: inFixed ? argb(PALETTE.ink) : argb('FFFFFF') });
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      } else if (r === 2) {
        cell.fill = inFixed ? solid(PALETTE.fixed) : solid(BAND_FILL[(bandOf(c) || {}).band] || PALETTE.fixed);
        cell.font = FONT({ bold: true, color: inFixed ? argb(PALETTE.ink) : argb('FFFFFF') });
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      } else if (r < dataStartRow) {
        // Special rows between header and data (COD Operator/COD %/COD Fixed, RTO Unit/Flat value)
        cell.fill = solid(PALETTE.special);
        cell.font = FONT({ bold: c === FIXED_COLS, color: argb(PALETTE.ink) });
        cell.border = THIN_BOTTOM;
        cell.alignment = { horizontal: inFixed ? 'right' : 'center' };
      } else {
        const even = (r - dataStartRow) % 2 === 1;
        if (inFixed) {
          cell.fill = solid(PALETTE.fixed);
          cell.font = FONT({ bold: c === 1, color: argb(PALETTE.ink) });
        } else {
          if (even) cell.fill = solid(PALETTE.zebra);
          cell.font = FONT();
        }
        cell.border = THIN_BOTTOM;
      }
    }
  }
  ws.columns.forEach((col, i) => { col.width = i < FIXED_COLS ? fixedWidth : 15; });
  ws.views = [{ state: 'frozen', xSplit: FIXED_COLS, ySplit: freezeRows }];
}

// Protect a sheet so only cells explicitly unlocked above remain editable.
// `allowRows` (custom sheets) additionally lets ops insert/delete rows for a
// ladder longer/shorter than the prefilled default.
async function protectSheet(ws, allowRows) {
  await ws.protect('', {
    selectLockedCells: true,
    selectUnlockedCells: true,
    insertRows: !!allowRows,
    deleteRows: !!allowRows,
  });
}

// Product-template band display names (acronyms stay uppercase).
const BAND_DISPLAY = { SURFACE: 'Surface', AIR: 'Air', SDD: 'SDD', NDD: 'NDD', REVERSE: 'Reverse' };

function bandHeaderRows(bands, firstHeader) {
  const bandRow = [null, null, null];
  const headRow = [firstHeader, firstHeader === 'Band' ? 'Min Order Value' : 'Min Slab', firstHeader === 'Band' ? 'Max Order Value' : 'Max Slab'];
  bands.forEach(({ band, columns }) => {
    columns.forEach((c, i) => {
      bandRow.push(i === 0 ? (BAND_DISPLAY[band] || band) : null);
      headRow.push(c.label);
    });
  });
  return [bandRow, headRow];
}

function matrixRows(bands, zones, slabs) {
  const rows = bandHeaderRows(bands, 'Zone');
  const width = FIXED_COLS + bands.reduce((n, b) => n + b.columns.length, 0);
  zones.forEach((z) => {
    slabs.forEach(([min, max]) => {
      const row = new Array(width).fill(null);
      row[0] = z.id; row[1] = min; row[2] = max;
      rows.push(row);
    });
  });
  return rows;
}

// Custom-sheet grid: the default ladder prefilled per zone as a starting point.
// No spare rows — the sheets are protected with row insertion ALLOWED, and every
// data cell (zone included) unlocks, so ops inserts rows for a longer ladder.
// The parser reads rows by their typed values, never by position.
function customMatrixRows(bands, zones, slabs) {
  return matrixRows(bands, zones, slabs);
}

const COD_BAND_ROWS = 6;

// T&C rows in the product template's 8-column layout and key order, with the
// product's Notes text kept verbatim wherever the key matches. Deviations from
// the product wording exist only where our flow genuinely differs: values are
// PREFILLED from the wizard UI and read-only here (the product template was a
// fill-in sheet), and its CLI/manual-fill phrases are dropped.
function tcRows(wizard, model) {
  const tc = (wizard.tc && wizard.tc.blended) || {};
  const f = (wizard.rvp && wizard.rvp.formulas && wizard.rvp.formulas.__blended__) || {};
  const gst = (wizard.agreement && wizard.agreement.gst_stance) || '';
  const formulaValue = f.type === 'mult' ? 'FWD_X_M' : (f.type === 'add' || f.type === 'flat') ? 'FLAT_PLUS_FWD' : 'HIGHER_OF';
  const rows = [
    ['Section', 'Key', 'Parameter', 'Value', 'Unit / Format', 'Required', 'Allowed Values', 'Notes'],
    ['[CARD]', null, 'Typed once; written into every row of every account. These values come from the wizard UI and are READ-ONLY here.', null, null, null, null, null],
    [null, 'merchant_oid', 'Merchant OID (ProShip)', wizard.merchantId || '', '24 hex chars', 'Y', '', 'The prozoId on every upload call and the folder name.'],
    [null, 'gst_stance', 'GST stance', gst ? gst.toUpperCase() : '', '', 'Y', 'EXCLUSIVE | INCLUSIVE', "INCLUSIVE stores Price as rate / 1.18. Real cards contradict themselves here: one says 'Taxes: Included in Commercial' in the rate table and 'GST charged over total freight' in the T&Cs, and guessing wrong moves every price by 18%."],
    [null, 'tax_pct', 'Tax (%)', 18, '%', 'Y', 'number', 'ProShip accepts only 0 or 18.'],
    [null, 'volumetric_coefficient', 'Volumetric coefficient', (wizard.agreement && wizard.agreement.vc) != null ? wizard.agreement.vc : '', '', 'Y', 'whole number', '(L x B x H) / this.'],
    [null, 'fuel_surcharge_pct', 'Fuel surcharge (%)', tc.fsc != null ? tc.fsc : 0, '%', 'Y', 'number', 'The live number. A card whose FSC recomputes monthly off diesel changes only this cell.'],
    [null, 'docket_charge', 'Docket charge', 0, 'INR', 'Y', 'number', ''],
    [null, 'rate_card_mode', 'Rate card mode', wizard.rateCardMode === 'blended' ? 'BLENDED' : 'COURIER_WISE', '', 'Y', 'BLENDED | COURIER_WISE', 'Blended or courier-wise is decided by the grid: one *ALL* column means blended.'],
    [null, 'cod_charge_mode', 'COD charge mode', model.codMode, '', 'Y', 'FIXED | VARIABLE', 'VARIABLE → fill the COD sheet.'],
    [null, 'rto_charge_mode', 'RTO charge mode', model.rtoMode, '', 'Y', 'FIXED | VARIABLE', 'VARIABLE → fill the RTO sheet (% of forward only).'],
    [null, 'custom_slab_parents', 'Custom-slab couriers', model.customParents.length ? model.customParents.join(', ') : '—', '', 'Y', '', model.customParents.length ? 'These parents live on the "(Custom)" sheets: type their shared slab ladder there (Zone/Min/Max are editable, one ladder for all zones and all of these parents; insert rows if it needs more slabs than prefilled). Everyone else uses the default ladder.' : 'All parents use the default slab ladder.'],
  ];
  // Band sections must cover every band present in the workbook, including bands
  // that exist ONLY on the "(Custom)" sheets — otherwise a merchant whose parents
  // are all custom-slab gets a T&C sheet with no band commercials at all.
  const tcBandNames = BAND_ORDER.filter((b) => model.bands.some((x) => x.band === b)
    || (model.customBands || []).some((x) => x.band === b));
  tcBandNames.forEach((band) => {
    rows.push([`[${band}]`, null, `Commercials for the ${band} band — fill the yellow Value cells from the agreement; they apply to every column of this band. Blended or courier-wise is decided by the grid: one *ALL* column means blended.`, null, null, null, null, null]);
    rows.push([null, 'enabled', 'Enabled', 'Y', '', 'Y', 'Y | N', 'N means this band emits no columns and provisions no accounts.']);
    const codPtr = model.codMode === 'VARIABLE';
    const rtoPtr = model.rtoMode === 'VARIABLE';
    const codNote = 'COD is Variable — fill per column on the COD sheet: the top Operator / COD % / COD Fixed rows for fixed, or the order-value bands (plain number = flat ₹; add % — e.g. 1.3% — for % of order value; bands may mix both).';
    rows.push([null, 'cod_operator', 'COD operator', codPtr ? '→ Fill on the COD sheet' : (tc.cod_op || 'MAX'), '', 'cond', 'MIN | MAX', codPtr ? codNote : '']);
    rows.push([null, 'cod_invoice_pct', 'COD invoice (%)', codPtr ? '→ Fill on the COD sheet' : (tc.cod_pct != null ? tc.cod_pct : 0), '%', 'cond', 'number', codPtr ? codNote : 'Must be 0 when cod_charge_type is VARIABLE.']);
    rows.push([null, 'cod_fixed_charge', 'COD fixed charge', codPtr ? '→ Fill on the COD sheet' : (tc.cod_fixed != null ? tc.cod_fixed : 0), 'INR', 'cond', 'number', codPtr ? codNote : 'Must be 0 when cod_charge_type is VARIABLE.']);
    rows.push([null, 'rto_mode', 'RTO mode', model.rtoMode === 'VARIABLE' ? 'TABLE' : 'PCT', '', 'cond', 'PCT | SAME_AS_FWD | TABLE', "'RTO included in commercial' can mean PCT 0 (free returns) or SAME_AS_FWD (100%). TABLE reads the RTO sheet."]);
    rows.push([null, 'rto_forward_pct', 'RTO (% of forward)', rtoPtr ? '→ Fill on the RTO sheet' : (tc.rto_pct != null ? tc.rto_pct : 0), '%', 'cond', 'number', rtoPtr ? 'RTO is Variable — % of forward per zone × slab on the RTO sheet.' : '']);
  });
  rows.push(['[REVERSE]', null, "Reverse terms apply to EVERY service, not only surface. RVP configuration comes from the wizard UI and is READ-ONLY here. A reverse account's COD and RTO are its forward sibling's; a REVERSE row on the COD or RTO sheet overrides that.", null, null, null, null, null]);
  rows.push([null, 'enabled', 'Enabled', model.rvpEnabled ? 'Y' : 'N', '', 'Y', 'Y | N', 'N means this band emits no columns and provisions no accounts.']);
  rows.push([null, 'rvp_enabled', 'RVP contracted', model.rvpEnabled ? 'Y' : 'N', '', 'Y', 'Y | N', '']);
  // Rows below only echo state their mode actually uses — never stale inputs
  // from another mode. RVP off masks everything; Mode B has no formula (RVP
  // prices come from the RVP sheet). QC splits by mode exactly like the
  // apply-time math: Mode A reads the formula (Commercials input), Mode B
  // reads rvp.tcs.qc_amount (the mandatory Stage-4 input).
  const rvpOff = !model.rvpEnabled;
  const rvpModeB = !rvpOff && model.rvpMode === 'independent';
  const naNote = rvpOff ? 'RVP is not contracted.' : 'Not used in Mode B — RVP prices come from the RVP sheet.';
  const maskFormula = rvpOff || rvpModeB;
  rows.push([null, 'rvp_mode', 'RVP mode', rvpOff ? '—' : (model.rvpMode === 'independent' ? 'INDEPENDENT' : 'FORWARD_LINKED'), '', 'cond', 'FORWARD_LINKED | INDEPENDENT', rvpOff ? naNote : "FORWARD_LINKED derives RVP from each account's own forward ladder — the workbook carries no RVP sheet. INDEPENDENT reads the RVP sheet's without-QC prices."]);
  rows.push([null, 'rvp_formula', 'RVP formula', maskFormula ? '—' : formulaValue, '', 'cond', 'HIGHER_OF | FLAT_PLUS_FWD | FWD_X_M', maskFormula ? naNote : 'HIGHER_OF = max(FWD + flat, FWD × M).']);
  rows.push([null, 'rvp_flat_add', 'RVP flat add (X)', maskFormula ? '—' : (f.flat != null ? f.flat : 0), 'INR', 'cond', 'number', maskFormula ? naNote : '']);
  rows.push([null, 'rvp_multiplier', 'RVP multiplier (M)', maskFormula ? '—' : (f.mult != null ? f.mult : 1), '×', 'cond', 'number', maskFormula ? naNote : 'A multiplier, not a percent: 1.5× is 1.5.']);
  const qcCharge = rvpOff ? '—' : (rvpModeB
    ? (Number(wizard.rvp && wizard.rvp.tcs && wizard.rvp.tcs.qc_amount) || 0)
    : (Number(f.qc) || 0));
  rows.push([null, 'rvp_qc_charge', 'QC charge', qcCharge, 'INR', 'cond', 'number', 'RVP With QC = RVP Without QC + this. ProShip carries one QC number; a tiered schedule (Rs 40 for 3 levels, +Rs 5 per level) enters its base here.']);
  return rows;
}

const TC_COLS = 8;
const TC_VALUE_COL = 4;

// Band-section commercial values the ops team fills from the agreement — the UI
// takes no per-service COD/RTO inputs, so these Value cells stay EDITABLE (and
// the upload reads them). [CARD] and [REVERSE] values come from the UI → locked.
const TC_EDITABLE_KEYS = new Set(['cod_operator', 'cod_invoice_pct', 'cod_fixed_charge', 'rto_forward_pct']);
const isBandSection = (section) => section && section !== '[CARD]' && section !== '[REVERSE]';

function paintTc(ws, model) {
  const codFixed = model.codMode !== 'VARIABLE';
  const rtoFixed = model.rtoMode !== 'VARIABLE';
  const keyEditable = (key) => (
    (key === 'rto_forward_pct' ? rtoFixed : codFixed) && TC_EDITABLE_KEYS.has(key)
  );
  const lastRow = ws.rowCount;
  let section = null;
  for (let r = 1; r <= lastRow; r += 1) {
    const row = ws.getRow(r);
    const aVal = String(row.getCell(1).value || '');
    const sectionRow = aVal.startsWith('[');
    if (sectionRow) section = aVal.trim();
    const key = String(row.getCell(2).value || '').trim();
    const editable = !sectionRow && isBandSection(section) && keyEditable(key);
    const pointer = !sectionRow && isBandSection(section) && TC_EDITABLE_KEYS.has(key) && !keyEditable(key);
    for (let c = 1; c <= TC_COLS; c += 1) {
      const cell = row.getCell(c);
      if (r === 1) {
        cell.fill = solid(PALETTE.ink);
        cell.font = FONT({ bold: true, color: argb('FFFFFF') });
        cell.border = { bottom: { style: 'medium', color: argb(PALETTE.ink) } };
      } else if (sectionRow) {
        cell.fill = solid(PALETTE.tcSection);
        cell.font = FONT({ bold: c <= 3, color: argb('FFFFFF') });
      } else {
        const even = r % 2 === 0;
        if (c === TC_VALUE_COL) {
          if (editable) {
            cell.protection = { locked: false };
            cell.fill = solid(PALETTE.mustfill);
            cell.font = FONT({ bold: true, color: argb(PALETTE.ink) });
          } else if (pointer) {
            cell.fill = solid(PALETTE.special);
            cell.font = FONT({ italic: true, color: argb('666666') });
          } else {
            cell.fill = solid(PALETTE.prefill);
            cell.font = FONT({ bold: true, color: argb(PALETTE.ink) });
          }
        } else {
          if (even) cell.fill = solid(PALETTE.zebra);
          cell.font = FONT({ color: argb(c === 2 ? '666666' : PALETTE.data) });
        }
        cell.border = THIN_BOTTOM;
        if (c === TC_COLS) cell.alignment = { wrapText: true, vertical: 'top' };
      }
    }
    // Product template merges the section description across C:H.
    if (sectionRow) ws.mergeCells(r, 3, r, TC_COLS);
  }
  // Product template widths: A15 B30 C34 D26 E13 F11 G32 H62.
  ws.columns = [
    { width: 15 }, { width: 30 }, { width: 34 }, { width: 26 },
    { width: 13 }, { width: 11 }, { width: 32 }, { width: 62 },
  ];
  ws.views = [{ state: 'frozen', xSplit: 3, ySplit: 1 }];
}

// Column-index maps for __META__: 1-based column index in the sheet → column def.
function indexedColumns(bands) {
  const out = [];
  let c = FIXED_COLS + 1;
  bands.forEach(({ band, columns }) => {
    columns.forEach((col) => {
      out.push({ c, band, label: col.label, all: !!col.all, entries: col.entries });
      c += 1;
    });
  });
  return out;
}

// Suggested download name: Prozo_RateCard_<Brand>_<YYYY-MM-DD>.xlsx
export function workbookFileNameV2(wizard) {
  const brand = (wizard.signup && (wizard.signup.brand || wizard.signup.business_name)) || 'merchant';
  const safe = String(brand).replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
  return `Prozo_RateCard_${safe}_${new Date().toISOString().slice(0, 10)}.xlsx`;
}

// Async: exceljs is loaded on demand (keeps it out of the main bundle) and
// returns the finished .xlsx as an ArrayBuffer/Buffer for download or tests.
export async function generateWorkbookV2(wizard) {
  const ExcelJSmod = await import('exceljs');
  const ExcelJS = ExcelJSmod.default || ExcelJSmod;
  const model = buildWorkbookModelV2(wizard);
  const wb = new ExcelJS.Workbook();

  // COD & RTO carry the forward bands PLUS a Reverse band (product-template layout:
  // a reverse account's COD/RTO default to its forward sibling's; a Reverse column
  // on these sheets overrides that).
  const chargeBands = model.rvpEnabled && model.rvpColumns.length
    ? [...model.bands, { band: 'REVERSE', columns: model.rvpColumns }]
    : model.bands;
  const customChargeBands = model.rvpEnabled && model.customRvpColumns.length
    ? [...model.customBands, { band: 'REVERSE', columns: model.customRvpColumns }]
    : model.customBands;

  const meta = {
    v: 2,
    merchantId: wizard.merchantId || null,
    slabTemplate: (wizard.rateCard && wizard.rateCard.slabTemplate) || null,
    rateCardMode: wizard.rateCardMode || 'courier_wise',
    codMode: model.codMode,
    rtoMode: model.rtoMode,
    rvpEnabled: model.rvpEnabled,
    rvpMode: model.rvpMode,
    zones: model.zones.map((z) => z.id),
    slabs: model.slabs,
    pricingColumns: indexedColumns(model.bands),
    rvpColumns: model.rvpEnabled ? indexedColumns([{ band: 'REVERSE', columns: model.rvpColumns }]) : [],
    chargeColumns: indexedColumns(chargeBands),
    customParents: model.customParents,
    customPricingColumns: indexedColumns(model.customBands),
    customRvpColumns: model.rvpEnabled ? indexedColumns([{ band: 'REVERSE', columns: model.customRvpColumns }]) : [],
    customChargeColumns: indexedColumns(customChargeBands),
  };

  // T&C — read-only reference (values live in the UI; the parser ignores this sheet).
  const tcWs = wb.addWorksheet(V2_SHEETS.TC);
  tcWs.addRows(tcRows(wizard, model));
  paintTc(tcWs, model);
  await protectSheet(tcWs);

  // Courier pricing — only the price cells are editable. Skipped entirely when
  // every selected parent is a custom-slab parent.
  if (model.bands.length) {
    const pricingWs = wb.addWorksheet(V2_SHEETS.PRICING);
    pricingWs.addRows(matrixRows(model.bands, model.zones, model.slabs));
    paintMatrix(pricingWs, model.bands, {
      dataStartRow: 3,
      freezeRows: 2,
      unlock: (r, c) => r >= 3 && c > FIXED_COLS,
    });
    await protectSheet(pricingWs);
  }

  // Courier pricing (Custom) — custom-slab parents only. The DEFAULT ladder is
  // prefilled as a starting point but Min/Max are editable (plus spare rows), so
  // ops can type the parents' real ladder; prices as usual. One shared ladder.
  if (model.customBands.length) {
    const ws = wb.addWorksheet(V2_SHEETS.PRICING_CUSTOM);
    ws.addRows(customMatrixRows(model.customBands, model.zones, model.slabs));
    paintMatrix(ws, model.customBands, {
      dataStartRow: 3,
      freezeRows: 2,
      // Whole data rows unlock (zone included): inserted rows inherit the row
      // above, so every cell ops must type into has to be editable.
      unlock: (r) => r >= 3,
    });
    await protectSheet(ws, true);
  }

  // RVP — Mode B only: fill the without-QC price per reverse courier × zone ×
  // slab. Mode A ships NO RVP sheet at all — reverse rates are computed at
  // upload from each courier's forward price via the Commercials formula.
  if (model.rvpEnabled && model.rvpMode === 'independent' && model.rvpColumns.length) {
    const rvpBands = [{ band: 'REVERSE', columns: model.rvpColumns }];
    const rvpWs = wb.addWorksheet(V2_SHEETS.RVP);
    rvpWs.addRows(matrixRows(rvpBands, model.zones, model.slabs));
    paintMatrix(rvpWs, rvpBands, {
      dataStartRow: 3,
      freezeRows: 2,
      unlock: (r, c) => r >= 3 && c > FIXED_COLS,
    });
    await protectSheet(rvpWs);
  }

  // RVP (Custom) — Mode B, custom-slab reverse parents: same editable-ladder
  // grid; its ladder must MATCH the Courier pricing (Custom) ladder on upload.
  if (model.rvpEnabled && model.rvpMode === 'independent' && model.customRvpColumns.length) {
    const rvpBands = [{ band: 'REVERSE', columns: model.customRvpColumns }];
    const ws = wb.addWorksheet(V2_SHEETS.RVP_CUSTOM);
    ws.addRows(customMatrixRows(rvpBands, model.zones, model.slabs));
    paintMatrix(ws, rvpBands, {
      dataStartRow: 3,
      freezeRows: 2,
      unlock: (r) => r >= 3,
    });
    await protectSheet(ws, true);
  }

  // COD (VARIABLE only) — product layout: Operator / COD % / COD Fixed rows
  // (blank per-column overrides), then the order-value bands. Editable cells:
  // the three override rows, the band Min/Max Order Value, and the band charges.
  // Order-value bands are already free-form, so the Custom variant differs only
  // in which parents' columns it carries.
  const addCodSheet = async (sheetName, bandsArg, custom) => {
    const codWs = wb.addWorksheet(sheetName);
    const rows = bandHeaderRows(bandsArg, 'Band');
    const width = FIXED_COLS + bandsArg.reduce((n, b) => n + b.columns.length, 0);
    ['Operator', 'COD %', 'COD Fixed'].forEach((label) => {
      const row = new Array(width).fill(null);
      row[2] = label;
      rows.push(row);
    });
    for (let b = 1; b <= COD_BAND_ROWS; b += 1) {
      const row = new Array(width).fill(null);
      row[0] = b;
      rows.push(row);
    }
    codWs.addRows(rows);
    paintMatrix(codWs, bandsArg, {
      dataStartRow: 6,
      freezeRows: 5,
      fixedWidth: 16,
      unlock: (r, c) => (r >= 3 && r <= 5 && c > FIXED_COLS) || (r >= 6 && (c === 2 || c === 3 || c > FIXED_COLS)),
    });
    await protectSheet(codWs, custom);
  };
  if (model.codMode === 'VARIABLE' && chargeBands.length) await addCodSheet(V2_SHEETS.COD, chargeBands, false);
  if (model.codMode === 'VARIABLE' && customChargeBands.length) await addCodSheet(V2_SHEETS.COD_CUSTOM, customChargeBands, true);

  // RTO (VARIABLE only) — product layout: Unit row (locked to %) + a Flat value
  // row that stays LOCKED and blank (flat ₹ RTO is not supported — decision D4),
  // then the zone × slab grid of percentages. The Custom variant rides the
  // editable custom ladder (must match the Courier pricing (Custom) ladder).
  const addRtoSheet = async (sheetName, bandsArg, custom) => {
    const rtoWs = wb.addWorksheet(sheetName);
    const rows = bandHeaderRows(bandsArg, 'Zone');
    const width = FIXED_COLS + bandsArg.reduce((n, b) => n + b.columns.length, 0);
    const unitRow = new Array(width).fill('%');
    unitRow[0] = null; unitRow[1] = null; unitRow[2] = 'Unit';
    rows.push(unitRow);
    const flatRow = new Array(width).fill(null);
    flatRow[2] = 'Flat value';
    rows.push(flatRow);
    model.zones.forEach((z) => {
      model.slabs.forEach(([min, max]) => {
        const row = new Array(width).fill(null);
        row[0] = z.id; row[1] = min; row[2] = max;
        rows.push(row);
      });
    });
    rtoWs.addRows(rows);
    paintMatrix(rtoWs, bandsArg, {
      dataStartRow: 5,
      freezeRows: 4,
      unlock: custom
        ? (r) => r >= 5
        : (r, c) => r >= 5 && c > FIXED_COLS,
    });
    await protectSheet(rtoWs, custom);
  };
  if (model.rtoMode === 'VARIABLE' && chargeBands.length) await addRtoSheet(V2_SHEETS.RTO, chargeBands, false);
  if (model.rtoMode === 'VARIABLE' && customChargeBands.length) await addRtoSheet(V2_SHEETS.RTO_CUSTOM, customChargeBands, true);

  // Hidden meta.
  const metaWs = wb.addWorksheet(V2_SHEETS.META);
  metaWs.addRow(['__META_V2__']);
  metaWs.addRow([JSON.stringify(meta)]);
  metaWs.state = 'veryHidden';

  return wb.xlsx.writeBuffer();
}

// ── Parse (SheetJS — unchanged model) ────────────────────────────────────────
const num = (v) => {
  if (v == null || String(v).trim() === '') return null;
  return isNaN(Number(v)) ? null : Number(v);
};

export function readMetaV2(wb) {
  const ws = wb.Sheets[V2_SHEETS.META];
  if (!ws) return null;
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  if (!rows[0] || rows[0][0] !== '__META_V2__') return null;
  try {
    const meta = JSON.parse(rows[1] && rows[1][0]);
    return meta && meta.v === 2 && Array.isArray(meta.pricingColumns) ? meta : null;
  } catch (e) {
    return null;
  }
}

// Reads one matrix sheet into { cellsByColIndex: {c: {'zone|min-max': value}}, badCells }.
function readMatrix(ws, dataStartRow) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  const cells = {};
  let badCells = 0;
  rows.slice(dataStartRow).forEach((row) => {
    const zone = num(row[0]);
    const min = num(row[1]);
    const max = num(row[2]);
    if (zone == null || max == null) return;
    for (let c = FIXED_COLS; c < row.length; c += 1) {
      const raw = row[c];
      if (raw == null || String(raw).trim() === '') continue;
      const v = num(raw);
      if (v == null) { badCells += 1; continue; }
      const key = `${zone}|${min}-${max}`;
      if (!cells[c + 1]) cells[c + 1] = {};
      cells[c + 1][key] = v;
    }
  });
  return { cells, badCells, headerRow: rows[1] || [] };
}

// The custom sheets' EDITABLE slab ladder, read from the typed Min/Max cells.
// Returns { perZone: {zoneId: [[min,max]...] sorted}, ladder } and pushes
// integrity errors: half-filled rows, data on rows with no ladder, non-zero
// start, non-contiguous slabs, and zone-to-zone differences (business rule:
// ONE custom ladder, identical for every zone and every custom parent).
function readCustomLadder(ws, dataStartRow, sheetName, errors, validZones) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  const perZone = {};
  let orphanDataRows = 0;
  let noZoneRows = 0;
  const unknownZones = new Set();
  rows.slice(dataStartRow).forEach((row) => {
    const zone = num(row[0]);
    const min = num(row[1]);
    const max = num(row[2]);
    const hasData = row.slice(FIXED_COLS).some((v) => v != null && String(v).trim() !== '');
    // Rows are ops-typed (insertion allowed), so incomplete rows must be loud.
    if (zone == null) {
      if (hasData || min != null || max != null) noZoneRows += 1;
      return;
    }
    if (validZones && !validZones.has(zone)) {
      if (hasData || min != null || max != null) unknownZones.add(zone);
      return;
    }
    if (min == null && max == null) {
      if (hasData) orphanDataRows += 1;
      return;
    }
    if (min == null || max == null) {
      errors.push(`"${sheetName}": zone ${zone} has a slab row with only one of Min/Max filled.`);
      return;
    }
    if (!(max > min)) {
      errors.push(`"${sheetName}": zone ${zone} slab ${min}-${max} — Max must be greater than Min.`);
      return;
    }
    (perZone[zone] = perZone[zone] || []).push([min, max]);
  });
  if (orphanDataRows) {
    errors.push(`"${sheetName}": ${orphanDataRows} row(s) carry values but no Min/Max slab — fill the ladder cells on those rows.`);
  }
  if (noZoneRows) {
    errors.push(`"${sheetName}": ${noZoneRows} row(s) carry values but no Zone — type the zone number on inserted rows.`);
  }
  if (unknownZones.size) {
    errors.push(`"${sheetName}": unknown zone(s) ${[...unknownZones].join(', ')} — this merchant's zones are ${validZones ? [...validZones].join(', ') : ''}.`);
  }
  Object.entries(perZone).forEach(([z, ladder]) => {
    ladder.sort((a, b) => a[0] - b[0]);
    if (ladder[0][0] !== 0) {
      errors.push(`"${sheetName}": zone ${z} ladder must start at 0 (got ${ladder[0][0]}).`);
    }
    for (let i = 1; i < ladder.length; i += 1) {
      if (ladder[i][0] !== ladder[i - 1][1]) {
        errors.push(`"${sheetName}": zone ${z} slabs ${ladder[i - 1][0]}-${ladder[i - 1][1]} and ${ladder[i][0]}-${ladder[i][1]} are not contiguous.`);
        break; // one report per zone is enough
      }
    }
  });
  const zoneIds = Object.keys(perZone);
  const sig = (l) => l.map(([a, b]) => `${a}-${b}`).join(',');
  if (zoneIds.length > 1) {
    const first = sig(perZone[zoneIds[0]]);
    zoneIds.slice(1).forEach((z) => {
      if (sig(perZone[z]) !== first) {
        errors.push(`"${sheetName}": zone ${z} ladder differs from zone ${zoneIds[0]} — the custom ladder must be identical across zones.`);
      }
    });
  }
  return { perZone, ladder: zoneIds.length ? perZone[zoneIds[0]] : [] };
}

// A COD charge cell, read from the raw cell object so percent entries survive:
// plain number → flat ₹ (FLAT); "1.3%" typed as TEXT → 1.3 PERCENTAGE; and the
// common Excel case — typing 1.3% makes a %-FORMATTED number storing 0.013 with
// display text "1.30%" — → 1.3 PERCENTAGE (raw × 100). Anything else → null.
function readChargeCell(ws, r, c) {
  const cell = ws[XLSX.utils.encode_cell({ r, c })];
  if (!cell || cell.v == null || String(cell.v).trim() === '') return null;
  if (cell.t === 'n') {
    if (/%\s*$/.test(String(cell.w || '')) || /%/.test(String(cell.z || ''))) {
      return { type: 'PERCENTAGE', value: +(cell.v * 100).toFixed(6) };
    }
    return Number.isFinite(cell.v) ? { type: 'FLAT', value: cell.v } : null;
  }
  const s = String(cell.v).trim();
  const m = s.match(/^(\d+(?:\.\d+)?)\s*%$/);
  if (m) return { type: 'PERCENTAGE', value: Number(m[1]) };
  const n = num(s);
  return n == null ? null : { type: 'FLAT', value: n };
}

// COD sheet (product layout: Operator / COD % / COD Fixed override rows, then
// order-value band rows) → per column index:
// { op, pct, fixed, slabs: [{minOrderValue, maxOrderValue, chargeType, chargeValue}] }.
// Band charges: plain number = flat ₹ (FLAT); a % entry (text "1.3%", or an
// Excel %-formatted cell) = percentage of order value (PERCENTAGE). Bands of
// one column may mix the two — each band is its own CodChargeSlab.
function readCodSheet(ws) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  const opRow = rows[2] || [];
  const pctRow = rows[3] || [];
  const fixRow = rows[4] || [];
  // The COD % row is a percent by definition: 1.3, "1.3%" and an Excel
  // %-formatted 1.3% (raw 0.013) must all land as 1.3.
  const pctOf = (c) => {
    const ch = readChargeCell(ws, 3, c);
    return ch == null ? null : ch.value;
  };
  const out = {};
  const errors = [];
  const colEntry = (c) => {
    if (!out[c + 1]) {
      out[c + 1] = {
        op: String(opRow[c] || '').trim().toUpperCase() || null,
        pct: pctOf(c),
        fixed: num(fixRow[c]),
        slabs: [],
      };
    }
    return out[c + 1];
  };
  rows.slice(5).forEach((row, i) => {
    const minOv = num(row[1]);
    const maxOv = num(row[2]);
    for (let c = FIXED_COLS; c < row.length; c += 1) {
      const ch = readChargeCell(ws, i + 5, c);
      if (ch == null) continue;
      if (minOv == null) { errors.push(`COD sheet: a value in column ${c + 1} has no Min Order Value on its band row.`); continue; }
      colEntry(c).slabs.push({ minOrderValue: minOv, maxOrderValue: maxOv, chargeType: ch.type, chargeValue: ch.value });
    }
  });
  // Pick up override-only columns too (op/%/fixed filled without band rows).
  for (let c = FIXED_COLS; c < Math.max(opRow.length, pctRow.length, fixRow.length); c += 1) {
    if ((opRow[c] != null && String(opRow[c]).trim() !== '') || pctOf(c) != null || num(fixRow[c]) != null) colEntry(c);
  }
  // Backend contract: slabs ordered by min order value, and the LAST slab is
  // open-ended (maxOrderValue null) — ops may bound every band; we open the tail.
  Object.values(out).forEach((entry) => {
    if (!entry.slabs.length) return;
    entry.slabs.sort((a, b) => a.minOrderValue - b.minOrderValue);
    entry.slabs[entry.slabs.length - 1].maxOrderValue = null;
  });
  return { byCol: out, errors };
}

// Band commercials typed on the T&C sheet (the UI has no per-service COD/RTO
// inputs, so the editable Value cells there are authoritative for their band):
// { SURFACE: { op, pct, fixed, rto }, AIR: {…}, … } — missing/blank → undefined.
function readTcBands(wb, meta) {
  const ws = wb.Sheets[V2_SHEETS.TC];
  const out = {};
  const errors = [];
  const codFromTc = meta.codMode !== 'VARIABLE';   // VARIABLE → COD sheet is authoritative
  const rtoFromTc = meta.rtoMode !== 'VARIABLE';   // VARIABLE → RTO sheet is authoritative
  if (!ws) return { bands: out, errors };
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  let section = null;
  rows.forEach((row) => {
    const a = String(row[0] || '').trim();
    if (a.startsWith('[')) { section = a.replace(/[[\]]/g, ''); return; }
    if (!section || section === 'CARD' || section === 'REVERSE') return;
    const key = String(row[1] || '').trim();
    const raw = row[3];
    if (!TC_EDITABLE_KEYS.has(key)) return;
    if (key === 'rto_forward_pct' ? !rtoFromTc : !codFromTc) return; // pointer text, not a value
    if (!out[section]) out[section] = {};
    if (key === 'cod_operator') {
      const v = String(raw || '').trim().toUpperCase();
      if (v && v !== 'MIN' && v !== 'MAX') errors.push(`T&C [${section}] cod_operator must be MIN or MAX (got "${raw}").`);
      else if (v) out[section].op = v;
    } else {
      const v = num(raw);
      if (raw != null && String(raw).trim() !== '' && v == null) errors.push(`T&C [${section}] ${key} must be a number (got "${raw}").`);
      else if (v != null) {
        if (key === 'cod_invoice_pct') out[section].pct = v;
        if (key === 'cod_fixed_charge') out[section].fixed = v;
        if (key === 'rto_forward_pct') out[section].rto = v;
      }
    }
  });
  return { bands: out, errors };
}

// Integrity guard: with every UI-derived cell locked, a value on an unknown
// (zone, slab) key can only mean the grid was tampered with after unprotecting.
function checkGridKeys(cells, validKeys, sheetName, errors) {
  const bad = new Set();
  Object.values(cells).forEach((byKey) => {
    Object.keys(byKey).forEach((k) => { if (!validKeys.has(k)) bad.add(k); });
  });
  if (bad.size) {
    errors.push(`"${sheetName}": ${bad.size} value(s) sit on altered zone/slab rows — the grid is fixed by the wizard; re-download the template.`);
  }
}

// Row skeleton shared by every emitted combo row: commercials come from the SAME
// resolver the legacy 17-column template used, so downstream maths are unchanged.
function baseRow(wizard, tcr, zone, min, max) {
  return {
    zone,
    start: min,
    min,
    max,
    price: null,
    addl_unit: 0,
    addl_rate: 0,
    vc: (wizard.agreement && wizard.agreement.vc) != null ? wizard.agreement.vc : null,
    tax: 18,
    fsc: tcr.fsc,
    docket: 0,
    cod_pct: tcr.cod_pct,
    cod_op: tcr.cod_op,
    cod_fixed: tcr.cod_fixed,
    rvp_noqc: null,
    rvp_qc: null,
    rto_pct: tcr.rto_pct,
  };
}

// Parse the V2 workbook back into the legacy combo model. Returns null when the
// file has no V2 meta (caller falls back to the legacy parser).
export function parseWorkbookV2(buf, wizard) {
  const wb = XLSX.read(buf, { type: 'array' });
  const meta = readMetaV2(wb);
  if (!meta) return null;

  const errors = [];
  const warnings = [];
  const missingSheets = [];
  let badPriceCells = 0;

  // The column split between the default and (Custom) sheets is baked in at
  // download time — a changed custom-slab selection needs a fresh download.
  const wizCsParents = (wizard.customSlabParents && wizard.customSlabParents.enabled
    && wizard.customSlabParents.parents) || [];
  const metaCsParents = meta.customParents || [];
  const csSig = (a) => [...a].sort().join('||');
  if (csSig(wizCsParents) !== csSig(metaCsParents)) {
    errors.push('The custom-slab courier selection changed after this workbook was downloaded — re-download the template.');
  }

  // The main pricing sheet is required only while it has columns (it is skipped
  // when every selected parent is a custom-slab parent).
  const pricingWs = wb.Sheets[V2_SHEETS.PRICING];
  if (!pricingWs && (meta.pricingColumns || []).length) {
    return {
      v2: true, combos: [], missingSheets: [V2_SHEETS.PRICING], badHeaderSheets: [], unknownSheets: [],
      badPriceCells: 0, metaSlabTemplate: meta.slabTemplate || null,
      errors: [`Sheet "${V2_SHEETS.PRICING}" is missing — re-download the template.`], warnings: [],
    };
  }
  const pricing = pricingWs ? readMatrix(pricingWs, 2) : { cells: {}, badCells: 0, headerRow: [] };
  badPriceCells += pricing.badCells;

  // Header drift + unknown-column detection (strict: the file must match its meta).
  const unknownColumns = [];
  const width = FIXED_COLS + meta.pricingColumns.length;
  meta.pricingColumns.forEach((col) => {
    const label = String(pricing.headerRow[col.c - 1] || '').trim();
    if (label && label !== col.label) warnings.push(`Column ${col.c} header changed ("${col.label}" → "${label}") — mapping still follows the downloaded template.`);
  });
  for (let c = width; c < pricing.headerRow.length; c += 1) {
    const label = String(pricing.headerRow[c] || '').trim();
    if (label) unknownColumns.push(label);
  }
  if (unknownColumns.length) {
    errors.push(`Unknown column(s) on "${V2_SHEETS.PRICING}": ${unknownColumns.join(', ')} — new couriers must be added on the Profile step, then re-download the template.`);
  }

  const zones = meta.zones && meta.zones.length ? meta.zones : ZONES.map((z) => z.id);
  const slabs = (meta.slabs && meta.slabs.length ? meta.slabs : slabsForTemplate(wizard));
  const validKeys = new Set();
  zones.forEach((zoneId) => slabs.forEach(([min, max]) => validKeys.add(`${zoneId}|${min}-${max}`)));
  checkGridKeys(pricing.cells, validKeys, V2_SHEETS.PRICING, errors);

  // COD / RTO columns live on their own sheets and include the Reverse band —
  // map (parent × service) → column index there via meta.chargeColumns.
  const chargeColumns = meta.chargeColumns || meta.pricingColumns;
  const chargeColOf = {};
  chargeColumns.forEach((col) => {
    (col.entries || []).forEach(({ parent, serviceId }) => { chargeColOf[`${parent}||${serviceId}`] = col.c; });
  });

  // COD sheet (required when the file was generated in VARIABLE mode).
  let codByCol = {};
  // The MAIN COD/RTO sheets only exist when there are non-custom columns to carry.
  // With every parent on the "(Custom)" sheets the generator correctly omits them, so
  // demanding them here rejected a workbook this very code had just produced — telling
  // the analyst to re-download the file they were holding.
  const hasDefaultChargeCols = (meta.chargeColumns || []).length > 0;
  if (meta.codMode === 'VARIABLE' && hasDefaultChargeCols) {
    const codWs = wb.Sheets[V2_SHEETS.COD];
    if (!codWs) {
      missingSheets.push(V2_SHEETS.COD);
      errors.push('COD is set to Variable but the COD sheet is missing — re-download the template.');
    } else {
      const cod = readCodSheet(codWs);
      codByCol = cod.byCol;
      errors.push(...cod.errors);
    }
  }

  // RTO sheet (required when generated in VARIABLE mode); values are % of forward.
  // The Flat value row exists for layout parity but must stay empty (decision D4).
  let rtoCells = {};
  if (meta.rtoMode === 'VARIABLE' && hasDefaultChargeCols) {
    const rtoWs = wb.Sheets[V2_SHEETS.RTO];
    if (!rtoWs) {
      missingSheets.push(V2_SHEETS.RTO);
      errors.push('RTO is set to Variable but the RTO sheet is missing — re-download the template.');
    } else {
      const rtoRows = XLSX.utils.sheet_to_json(rtoWs, { header: 1, defval: '' });
      const unitRow = rtoRows[2] || [];
      const flatRow = rtoRows[3] || [];
      chargeColumns.forEach((col) => {
        const u = String(unitRow[col.c - 1] || '%').trim();
        if (u && u !== '%') errors.push(`RTO sheet: Unit for "${col.label}" must be % (flat ₹ RTO is not supported — MoM decision D4).`);
        if (num(flatRow[col.c - 1]) != null) errors.push(`RTO sheet: "Flat value" is filled for "${col.label}" — flat ₹ RTO is not supported (MoM decision D4); use % of forward.`);
      });
      const rto = readMatrix(rtoWs, 4);
      rtoCells = rto.cells;
      checkGridKeys(rtoCells, validKeys, V2_SHEETS.RTO, errors);
    }
  }

  const qcAmt = Number(wizard.rvp && wizard.rvp.tcs && wizard.rvp.tcs.qc_amount) || 0;

  // Applies the COD/RTO sheet columns onto one combo's rows; returns whether the
  // combo needs-but-lacks COD bands / RTO values (checked only for priced combos).
  // `src` swaps in the (Custom) sheets' maps for custom-slab combos.
  const applyCharges = (rows, parent, serviceId, label, optional, src) => {
    const maps = src || { colOf: chargeColOf, cod: codByCol, rto: rtoCells };
    const cc = maps.colOf[`${parent}||${serviceId}`];
    const cod = meta.codMode === 'VARIABLE' ? maps.cod[cc] : null;
    const rtoCol = meta.rtoMode === 'VARIABLE' ? (maps.rto[cc] || {}) : {};
    rows.forEach((row) => {
      if (cod) {
        if (cod.op) row.cod_op = cod.op;
        if (cod.slabs.length) {
          row.cod_charge_type = 'VARIABLE';
          row.cod_slabs = cod.slabs;
          row.cod_pct = 0; row.cod_fixed = 0;
        } else {
          if (cod.pct != null) row.cod_pct = cod.pct;
          if (cod.fixed != null) row.cod_fixed = cod.fixed;
        }
      }
      const key = `${row.zone}|${row.min}-${row.max}`;
      if (meta.rtoMode === 'VARIABLE' && rtoCol[key] != null) row.rto_pct = rtoCol[key];
    });
    if (optional) return; // Reverse columns are overrides — forward sibling's charges apply by default.
    const priced = rows.some((r) => r.price != null);
    // Per-column mixing is allowed: a courier is FIXED when only the top
    // Operator / COD % / COD Fixed rows are filled, VARIABLE when its bands are
    // filled (bands win if both). Error only when a priced courier has neither.
    const hasFixedTop = cod && (cod.pct != null || cod.fixed != null);
    if (priced && meta.codMode === 'VARIABLE' && !(cod && cod.slabs.length) && !hasFixedTop) {
      errors.push(`COD is Variable but neither COD bands nor the fixed Operator/COD %/COD Fixed rows are filled for "${label}".`);
    }
    if (priced && meta.rtoMode === 'VARIABLE' && !Object.keys(rtoCol).length) {
      errors.push(`RTO is Variable but the RTO sheet has no values for "${label}".`);
    }
  };

  // Band commercials typed on the T&C sheet (COD operator/% / fixed, RTO %) —
  // they apply to every column of their band; wizard defaults fill the gaps.
  const tcBands = readTcBands(wb, meta);
  errors.push(...tcBands.errors);

  // ── Forward combos ──
  const combos = [];
  meta.pricingColumns.forEach((col) => {
    const colCells = pricing.cells[col.c] || {};
    const bandTc = tcBands.bands[col.band] || {};
    (col.entries || []).forEach((entry) => {
      const { parent, serviceId } = entry;
      const tcr = resolveTcForParent(wizard, parent, serviceId);
      const rows = [];
      zones.forEach((zoneId) => {
        slabs.forEach(([min, max]) => {
          const key = `${zoneId}|${min}-${max}`;
          const row = baseRow(wizard, tcr, zoneId, min, max);
          row.price = colCells[key] != null ? colCells[key] : null;
          if (bandTc.op != null) row.cod_op = bandTc.op;
          if (bandTc.pct != null) row.cod_pct = bandTc.pct;
          if (bandTc.fixed != null) row.cod_fixed = bandTc.fixed;
          if (bandTc.rto != null) row.rto_pct = bandTc.rto;
          rows.push(row);
        });
      });
      applyCharges(rows, parent, serviceId, col.label);
      combos.push({ parent, serviceId, kind: 'forward', rows });
    });
  });

  // ── RVP combos (Mode B only — Mode A derives from the forward ladder) ──
  if (meta.rvpEnabled && meta.rvpMode === 'independent' && (meta.rvpColumns || []).length) {
    const rvpWs = wb.Sheets[V2_SHEETS.RVP];
    if (!rvpWs) {
      missingSheets.push(V2_SHEETS.RVP);
      warnings.push('RVP sheet missing — reverse pricing left unchanged.');
    } else {
      const rvpM = readMatrix(rvpWs, 2);
      badPriceCells += rvpM.badCells;
      checkGridKeys(rvpM.cells, validKeys, V2_SHEETS.RVP, errors);
      meta.rvpColumns.forEach((col) => {
        const colCells = rvpM.cells[col.c] || {};
        (col.entries || []).forEach((entry) => {
          const { parent, serviceId } = entry;
          const tcr = resolveTcForParent(wizard, parent, serviceId);
          const rows = [];
          zones.forEach((zoneId) => {
            slabs.forEach(([min, max]) => {
              const v = colCells[`${zoneId}|${min}-${max}`];
              if (v == null) return;
              const row = baseRow(wizard, tcr, zoneId, min, max);
              // The RVP cell IS the without-QC price; with-QC adds the UI QC charge.
              row.price = v;
              row.rvp_noqc = v;
              row.rvp_qc = +(v + qcAmt).toFixed(2);
              rows.push(row);
            });
          });
          // Reverse columns on the COD/RTO sheets override the forward sibling's charges.
          applyCharges(rows, parent, serviceId, col.label, true);
          combos.push({ parent, serviceId, kind: 'rvp', rows });
        });
      });
    }
  }

  // ── Custom-slab combos — the "(Custom)" sheets, whose ladder ops typed ──
  const customPricingCols = meta.customPricingColumns || [];
  const customRvpCols = meta.customRvpColumns || [];
  if (customPricingCols.length || customRvpCols.length) {
    // Ladder + prices from Courier pricing (Custom).
    let customLadder = { perZone: {}, ladder: [] };
    let customCells = {};
    if (customPricingCols.length) {
      const ws = wb.Sheets[V2_SHEETS.PRICING_CUSTOM];
      if (!ws) {
        missingSheets.push(V2_SHEETS.PRICING_CUSTOM);
        errors.push(`Sheet "${V2_SHEETS.PRICING_CUSTOM}" is missing — re-download the template.`);
      } else {
        customLadder = readCustomLadder(ws, 2, V2_SHEETS.PRICING_CUSTOM, errors, new Set(zones));
        const m = readMatrix(ws, 2);
        customCells = m.cells;
        badPriceCells += m.badCells;
      }
    }
    const customValidKeys = new Set();
    Object.entries(customLadder.perZone).forEach(([z, ladder]) => {
      ladder.forEach(([mn, mx]) => customValidKeys.add(`${z}|${mn}-${mx}`));
    });
    const ladderMatchGuard = (cells, sheetName) => {
      const bad = new Set();
      Object.values(cells).forEach((byKey) => {
        Object.keys(byKey).forEach((k) => { if (!customValidKeys.has(k)) bad.add(k); });
      });
      if (bad.size) {
        errors.push(`"${sheetName}": ${bad.size} value(s) sit on zone/slab rows that do not match the "${V2_SHEETS.PRICING_CUSTOM}" ladder — keep the ladders identical.`);
      }
    };

    // (Custom) COD / RTO sheets for the custom parents' charge columns.
    const customChargeCols = meta.customChargeColumns || customPricingCols;
    const customChargeColOf = {};
    customChargeCols.forEach((col) => {
      (col.entries || []).forEach(({ parent, serviceId }) => { customChargeColOf[`${parent}||${serviceId}`] = col.c; });
    });
    let codByColCustom = {};
    if (meta.codMode === 'VARIABLE' && customChargeCols.length) {
      const ws = wb.Sheets[V2_SHEETS.COD_CUSTOM];
      if (!ws) {
        missingSheets.push(V2_SHEETS.COD_CUSTOM);
        errors.push(`COD is Variable but the "${V2_SHEETS.COD_CUSTOM}" sheet is missing — re-download the template.`);
      } else {
        const cod = readCodSheet(ws);
        codByColCustom = cod.byCol;
        errors.push(...cod.errors);
      }
    }
    let rtoCellsCustom = {};
    if (meta.rtoMode === 'VARIABLE' && customChargeCols.length) {
      const ws = wb.Sheets[V2_SHEETS.RTO_CUSTOM];
      if (!ws) {
        missingSheets.push(V2_SHEETS.RTO_CUSTOM);
        errors.push(`RTO is Variable but the "${V2_SHEETS.RTO_CUSTOM}" sheet is missing — re-download the template.`);
      } else {
        const rtoRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        const unitRow = rtoRows[2] || [];
        const flatRow = rtoRows[3] || [];
        customChargeCols.forEach((col) => {
          const u = String(unitRow[col.c - 1] || '%').trim();
          if (u && u !== '%') errors.push(`"${V2_SHEETS.RTO_CUSTOM}": Unit for "${col.label}" must be % (flat ₹ RTO is not supported — MoM decision D4).`);
          if (num(flatRow[col.c - 1]) != null) errors.push(`"${V2_SHEETS.RTO_CUSTOM}": "Flat value" is filled for "${col.label}" — flat ₹ RTO is not supported (MoM decision D4); use % of forward.`);
        });
        const rto = readMatrix(ws, 4);
        rtoCellsCustom = rto.cells;
        ladderMatchGuard(rtoCellsCustom, V2_SHEETS.RTO_CUSTOM);
      }
    }
    const customSrc = { colOf: customChargeColOf, cod: codByColCustom, rto: rtoCellsCustom };

    // Forward combos over the CUSTOM ladder (zones without a typed ladder emit
    // no rows — same semantics as leaving a zone unpriced on the main sheet).
    customPricingCols.forEach((col) => {
      const colCells = customCells[col.c] || {};
      const bandTc = tcBands.bands[col.band] || {};
      (col.entries || []).forEach((entry) => {
        const { parent, serviceId } = entry;
        const tcr = resolveTcForParent(wizard, parent, serviceId);
        const rows = [];
        zones.forEach((zoneId) => {
          (customLadder.perZone[zoneId] || []).forEach(([min, max]) => {
            const key = `${zoneId}|${min}-${max}`;
            const row = baseRow(wizard, tcr, zoneId, min, max);
            row.price = colCells[key] != null ? colCells[key] : null;
            if (bandTc.op != null) row.cod_op = bandTc.op;
            if (bandTc.pct != null) row.cod_pct = bandTc.pct;
            if (bandTc.fixed != null) row.cod_fixed = bandTc.fixed;
            if (bandTc.rto != null) row.rto_pct = bandTc.rto;
            rows.push(row);
          });
        });
        applyCharges(rows, parent, serviceId, col.label, false, customSrc);
        combos.push({ parent, serviceId, kind: 'forward', rows });
      });
    });

    // RVP (Custom) combos — Mode B only; ladder must match the custom pricing ladder.
    if (meta.rvpEnabled && meta.rvpMode === 'independent' && customRvpCols.length) {
      const ws = wb.Sheets[V2_SHEETS.RVP_CUSTOM];
      if (!ws) {
        missingSheets.push(V2_SHEETS.RVP_CUSTOM);
        warnings.push(`"${V2_SHEETS.RVP_CUSTOM}" sheet missing — reverse pricing left unchanged.`);
      } else {
        const m = readMatrix(ws, 2);
        badPriceCells += m.badCells;
        ladderMatchGuard(m.cells, V2_SHEETS.RVP_CUSTOM);
        customRvpCols.forEach((col) => {
          const colCells = m.cells[col.c] || {};
          (col.entries || []).forEach((entry) => {
            const { parent, serviceId } = entry;
            const tcr = resolveTcForParent(wizard, parent, serviceId);
            const rows = [];
            zones.forEach((zoneId) => {
              (customLadder.perZone[zoneId] || []).forEach(([min, max]) => {
                const v = colCells[`${zoneId}|${min}-${max}`];
                if (v == null) return;
                const row = baseRow(wizard, tcr, zoneId, min, max);
                // The RVP cell IS the without-QC price; with-QC adds the UI QC charge.
                row.price = v;
                row.rvp_noqc = v;
                row.rvp_qc = +(v + qcAmt).toFixed(2);
                rows.push(row);
              });
            });
            applyCharges(rows, parent, serviceId, col.label, true, customSrc);
            combos.push({ parent, serviceId, kind: 'rvp', rows });
          });
        });
      }
    }
  }

  return {
    v2: true,
    combos,
    missingSheets,
    badHeaderSheets: [],
    unknownSheets: [],
    badPriceCells,
    metaSlabTemplate: meta.slabTemplate || null,
    codMode: meta.codMode,
    rtoMode: meta.rtoMode,
    errors,
    warnings,
  };
}
